import { DeleteOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Form,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  message
} from 'antd';
import { isEmpty } from 'lodash';
import lodashClonedeep from 'lodash/cloneDeep';
import React, { useCallback, useEffect, useState } from 'react';
import CurrencyInputCustom from '../../components/CurrencyInputCustom';
import { InputIconEdit } from '../../components/InputIcons';
import { FRANQUICIA_DESDE_HASTA } from '../../constants';
import FranquiciaSelect from '../../modelos/admins/componentes/FranquiciaSelect';
import {
  currencyToNumber,
  errorHandler,
  getDescripcionFranquicia,
  renderPorcentaje,
  transformFranquiciaData
} from '../../utils';
import { instanceRef } from '../../utils/axios';
import { formatoMonedaGenerico } from '../../utils/converters';

const FranquiciaPorRangos = ({
  open,
  onOk,
  onCancel,
  model = {},
  idAseguradora
}) => {
  const [franquiciaRangoList, setFranquiciaRangoList] = useState([]);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [franquiciaConfig, setFranquiciaConfig] = useState();
  const [modalForm] = Form.useForm();

  const formDialogLayout = {
    labelCol: {
      xs: { span: 12 }
    },
    wrapperCol: {
      xs: { span: 12 }
    }
  };

  const prepareData = (listadoFranquicias) => {
    const franquicias = [];

    for (let i = 0; i < listadoFranquicias.length; i += 1) {
      const values = listadoFranquicias[i];
      const franquiciaOpcion = values.opcion === '' ? null : values.opcion;
      const preparedData = {
        ...values,
        franquicia_opcion: franquiciaOpcion,
        franquicia_monto: values.monto,
        franquicia_desde: values.desde,
        franquicia_hasta: values.hasta,
        //  opcion: franquiciaOpcion,
        ...(values.monto
          ? {
              monto: Number(values.monto || '0')
            }
          : {}),
        ...(values.costo_min
          ? {
              costo_min: Number(values.costo_min || '0')
            }
          : {}),
        ...(values.costo_max
          ? {
              costo_max: Number(values.costo_max || '0')
            }
          : {})
      };
      preparedData.descripcion = getDescripcionFranquicia(
        preparedData,
        model.moneda
      );
      franquicias.push(preparedData);
    }

    return {
      ...model,
      franquicia_config: franquiciaConfig?.data,
      franquicias
    };
  };

  const onFinish = () => {
    const result = prepareData(franquiciaRangoList);
    onOk(result);
  };

  const handleAdd = (values) => {
    const newData = {
      key: `tipo-${Math.random().toString(36).substring(2, 9)}`,
      row: 1, // set row to 1
      ...values,
      monto: values.monto || 0,
      descripcion: (() => {
        const transformedData = transformFranquiciaData(values);
        return getDescripcionFranquicia(transformedData, model.moneda);
      })()
    };

    const newFranquiciaRangoList = [newData, ...franquiciaRangoList];

    const updatedFranquiciaRangoList = newFranquiciaRangoList.map(
      (item, index) => ({
        ...item,
        row: index + 1
      })
    );

    setFranquiciaRangoList(updatedFranquiciaRangoList);
  };

  const handleUpdate = (values) => {
    const index = franquiciaRangoList.findIndex(
      (elem) => elem.row === values.row
    );

    let updatedItem;

    if (index !== -1)
      // Verifica si se encontró un índice válido
      updatedItem = {
        ...(values.key && { key: values.key }),
        row: values.row,
        ...values,
        editado: true,
        monto: values.monto || 0,
        descripcion: (() => {
          const transformedData = transformFranquiciaData(values);
          return getDescripcionFranquicia(transformedData, model.moneda);
        })()
      };

    franquiciaRangoList[index] = updatedItem;

    setFranquiciaRangoList([...franquiciaRangoList]); // Actualiza el estado con una nueva referencia de lista
  };

  const handleDelete = (identificador) => {
    const index = franquiciaRangoList.findIndex(
      (elem) => elem.row === identificador
    );

    franquiciaRangoList[index].eliminado = true;

    const newFranquiciaRangoList = [...franquiciaRangoList];

    for (let i = 0; i < newFranquiciaRangoList.length; i += 1) {
      if (!newFranquiciaRangoList[i].eliminado)
        newFranquiciaRangoList[i].row = i + 1;
    }

    setFranquiciaRangoList([...newFranquiciaRangoList]); // Actualiza el estado con una nueva referencia de lista
  };

  const mapOptions = useCallback(
    (data) => ({
      value: data.parcial_franquicia,
      label: `(${data.parcial_franquicia}) ${
        data.tipo_franquicia === 'F'
          ? formatoMonedaGenerico(model.moneda, data.franquicia || '0')
          : data.franquicia + data.descripcion
      }`,
      data
    }),
    [model.moneda]
  );

  const fetchList = useCallback(async () => {
    const response = await instanceRef.instance.get(`/sebaot/franquicias`, {
      params: { idAseguradora, codigo_producto: model.producto?.codigo, model }
    });
    return response;
  }, [idAseguradora, model]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchList();
      if (response.data.data) {
        setOptions(response.data.data.map(mapOptions));
      } else {
        message.error('No se pudo cargar la lista de franquicias');
      }
      setLoading(false);
    } catch (error) {
      errorHandler(error);
      setLoading(false);
    }
  }, [fetchList, mapOptions]);

  useEffect(() => {
    if (model.producto?.codigo) {
      // Settear datos de la tabla
      const lista = [...model.franquicias];
      for (let i = 0; i < lista.length; i += 1) {
        lista[i].row = i + 1;
      }
      setFranquiciaRangoList(model.franquicias);
      modalForm.setFieldsValue({
        franquicia_config: !isEmpty(model.franquicia_config)
          ? model.franquicia_config
          : undefined
      });
      loadData();
    }
  }, [model, modalForm, loadData]);

  const columns = [
    {
      title: 'Rango',
      render: (_, record) => {
        const { row } = record;
        return <>{row}</>;
      }
    },
    {
      title: 'Desde',
      render: (_, record) => {
        const { desde } = record;
        return <>{desde ? `Desde ${desde}º Siniestro` : ''}</>;
      }
    },
    {
      title: 'Hasta',
      render: (_, record) => {
        const { hasta } = record;
        return <>{hasta ? `Hasta ${hasta}º Siniestro` : ''}</>;
      }
    },
    {
      title: 'Tipo',
      render: (_, record) => {
        const tipo = record.opcion;
        if (tipo === 'monto_fijo') return <>Monto fijo</>;
        if (tipo === 'valor_siniestro') return <>Sobre Siniestro</>;
        if (tipo === 'suma_asegurada') return <>Sobre Suma Asegurada</>;
        return <></>;
      }
    },
    {
      title: 'Monto',
      render: (_, record) => {
        const { monto } = record;
        return monto && formatoMonedaGenerico(model.moneda, monto);
      }
    },
    {
      title: 'Porcentaje',
      render: (_, record) => {
        const porcentaje = record.criterio;
        return renderPorcentaje(porcentaje);
      }
    },
    {
      title: 'Mínimo',
      render: (_, record) => {
        const minimo = record.costo_min;
        return minimo && formatoMonedaGenerico(model.moneda, minimo);
      }
    },
    {
      title: 'Máximo',
      render: (_, record) => {
        const maximo = record.costo_max;
        return maximo && formatoMonedaGenerico(model.moneda, maximo);
      }
    },
    {
      title: 'Descripción',
      render: (_, record) => {
        const { descripcion } = record;
        return <>{descripcion || ''}</>;
      }
    },
    {
      title: 'Acciones',
      key: 'action',
      width: '10px',
      align: 'left',
      render: (_, record) => {
        return (
          <Space>
            <Tooltip title="Editar">
              <FormModal
                columnName="configurar"
                title="Configuración de Franquicia"
                button="editar"
                initialValues={record}
                handleAceptar={(values) => handleUpdate(values)}
              />
            </Tooltip>
            <Tooltip title="Eliminar">
              <DeleteOutlined
                style={{ color: 'rgba(0,0,0,.45)' }}
                onClick={() => handleDelete(record.row)}
              />
            </Tooltip>
          </Space>
        );
      }
    }
  ];

  const FormModal = ({
    columnName = '',
    initialValues,
    button,
    handleAceptar
  }) => {
    const [form] = Form.useForm();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const validarForm = async () => {
      const response = await form
        .validateFields()
        .then(() => true)
        .catch(() => false);

      return response;
    };

    const handleOk = async () => {
      const formValidado = await validarForm();
      if (formValidado) {
        const values = form.getFieldsValue();
        if (values.opcion)
          handleAceptar({
            id: initialValues?.id,
            row: initialValues?.row,
            ...values,
            franquicia_config: franquiciaConfig
          });
        setIsModalOpen(false);
      }
    };

    const handleCancel = () => {
      clearAll();
      setIsModalOpen(false);
    };
    const clearAll = () => {};

    useEffect(() => {
      if (initialValues && !initialValues.eliminado) {
        form.setFieldsValue(initialValues);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValues]);

    return (
      <>
        {button === 'editar' ? (
          <InputIconEdit onClick={() => setIsModalOpen(!isModalOpen)} />
        ) : (
          <Button
            onClick={() => setIsModalOpen(!isModalOpen)}
            style={{ marginBottom: '1%' }}
          >
            Agregar
          </Button>
        )}
        {model && (
          <Modal
            open={isModalOpen}
            onOk={handleOk}
            onCancel={handleCancel}
            width={700}
            afterClose={() => {
              form.resetFields();
            }}
            okText={columnName === 'eliminar' && 'Si'}
            cancelText={columnName === 'eliminar' && 'No'}
          >
            <Form
              {...formDialogLayout}
              scrollToFirstError
              id="franquiciaEditor"
              name="franquiciaEditor"
              form={form}
              autoComplete="off"
              layout="horizontal"
              style={{ margin: '2%' }}
            >
              <Form.Item name="desde" label="Desde">
                <Select
                  showSearch
                  allowClear
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: '100%' }}
                  optionFilterProp="children"
                  placeholder="Seleccionar Desde"
                >
                  {FRANQUICIA_DESDE_HASTA.map((item) => (
                    <Select.Option
                      key={`${item.codigo}`}
                      value={`${item.codigo}`}
                    >
                      {item.descripcion}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="hasta" label="Hasta">
                <Select
                  showSearch
                  allowClear
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: '100%' }}
                  optionFilterProp="children"
                  placeholder="Seleccionar Hasta"
                >
                  {FRANQUICIA_DESDE_HASTA.map((item) => (
                    <Select.Option
                      key={`${item.codigo}`}
                      value={`${item.codigo}`}
                    >
                      {item.descripcion}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="opcion"
                label="Franquicia"
                rules={[{ required: true, message: 'Requerido' }]}
              >
                <Radio.Group>
                  <Space direction="vertical">
                    <Radio value="">Sin Franquicia</Radio>
                    <Radio value="monto_fijo">Monto Fijo</Radio>
                    <Radio value="valor_siniestro">Sobre Siniestro</Radio>
                    <Radio value="suma_asegurada">Sobre Suma Asegurada</Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, curValues) =>
                  prevValues.opcion !== curValues.opcion
                }
              >
                {({ getFieldValue }) => {
                  return (
                    <>
                      {getFieldValue('opcion') === 'monto_fijo' && (
                        <Form.Item
                          label="Monto"
                          name="monto"
                          normalize={(value) =>
                            currencyToNumber(value, model.moneda)
                          }
                        >
                          <CurrencyInputCustom moneda={model.moneda} bordered />
                        </Form.Item>
                      )}
                      {(getFieldValue('opcion') === 'valor_siniestro' ||
                        getFieldValue('opcion') === 'suma_asegurada') && (
                        <Form.Item label="Porcentaje" name="criterio">
                          <InputNumber
                            min={0}
                            max={100}
                            formatter={(value) => `${value}%`}
                            parser={(value) => value.replace('%', '')}
                            decimalSeparator=","
                          />
                        </Form.Item>
                      )}
                      {(getFieldValue('opcion') === 'valor_siniestro' ||
                        getFieldValue('opcion') === 'suma_asegurada') && (
                        <>
                          <Form.Item
                            label="Mínimo"
                            name="costo_min"
                            normalize={(value) =>
                              currencyToNumber(value, model.moneda)
                            }
                          >
                            <CurrencyInputCustom
                              moneda={model.moneda}
                              bordered
                            />
                          </Form.Item>
                          <Form.Item
                            label="Máximo"
                            name="costo_max"
                            normalize={(value) =>
                              currencyToNumber(value, model.moneda)
                            }
                          >
                            <CurrencyInputCustom
                              moneda={model.moneda}
                              bordered
                            />
                          </Form.Item>
                        </>
                      )}
                    </>
                  );
                }}
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, curValues) =>
                  prevValues.opcion !== curValues.opcion ||
                  prevValues.monto !== curValues.monto ||
                  prevValues.costo_min !== curValues.costo_min ||
                  prevValues.costo_max !== curValues.costo_max ||
                  prevValues.criterio !== curValues.criterio
                }
              >
                {({ getFieldValue }) => {
                  return (
                    <>
                      {getFieldValue('opcion') !== '' && (
                        <>
                          <Form.Item label="Descripción" name="descripcion">
                            <span>
                              {getDescripcionFranquicia(
                                transformFranquiciaData(getFieldValue()),
                                model.moneda
                              )}
                            </span>
                          </Form.Item>
                        </>
                      )}
                    </>
                  );
                }}
              </Form.Item>
            </Form>
          </Modal>
        )}
      </>
    );
  };

  return (
    <>
      <>
        <Modal
          title="Configuración de Franquicia General"
          open={open}
          onCancel={() => {
            onCancel(false);
          }}
          afterClose={() => {
            modalForm.resetFields();
          }}
          onOk={onFinish}
          width={1300}
        >
          <Form
            {...formDialogLayout}
            scrollToFirstError
            id="franquiciaSistemaCore"
            name="franquiciaSistemaCore"
            form={modalForm}
            autoComplete="off"
            layout="vertical"
          >
            {' '}
            <Row>
              <Col md={12} xs={24}>
                {' '}
                <Form.Item
                  label="Franquicia Sistema Core"
                  name="franquicia_config"
                >
                  <FranquiciaSelect
                    loading={loading}
                    options={options}
                    onSelect={(option) => {
                      let franquicia = options.find(
                        (item) => item.value === option.value
                      );
                      franquicia = lodashClonedeep(franquicia);
                      setFranquiciaConfig(franquicia);
                    }}
                  />
                </Form.Item>
              </Col>{' '}
            </Row>
          </Form>
          <div style={{ textAlign: 'left' }}>
            <Tooltip title="Agregar franquicia">
              <FormModal
                columnName="configurar"
                title="Configuración de Franquicia"
                button="agregar"
                handleAceptar={(values) => handleAdd(values)}
              />
            </Tooltip>
            <Table
              fixed
              scroll={false}
              bordered
              dataSource={franquiciaRangoList.filter((elem) => !elem.eliminado)}
              columns={columns}
              pagination={false}
              rowKey={(record) =>
                `${
                  record && (record.id || record.row)
                }-tipo-${Math.random().toString(36).substr(2, 9)}`
              }
              size="small"
              width="auto"
            />
          </div>
        </Modal>
      </>
    </>
  );
};

export default FranquiciaPorRangos;
